import { useState } from 'react';
import {
  Radio,
  CheckCircle2,
  AlertCircle,
  RotateCw,
  Copy,
  Check,
  Smartphone,
  Monitor,
  ArrowDownUp,
  HelpCircle,
} from 'lucide-react';
import { useSerialBridge } from '../hooks/useSerialBridge';

export function SerialeRemotaSection() {
  const { status, loading, error, resetting, refresh, resetBridge } = useSerialBridge();
  const [copied, setCopied] = useState(false);

  // Host del server dedotto dall'URL attuale (o default danye.mywire.org)
  const host = typeof window !== 'undefined' && window.location.hostname
    ? window.location.hostname
    : 'danye.mywire.org';

  const squadraPort = status?.squadraPort || 10001;
  const operatorePort = status?.operatorePort || 10000;

  const isSquadraConnected = Boolean(status?.squadraConnected);
  const isOperatoreConnected = Boolean(status?.operatoreConnected);
  const isGraceActive = Boolean(status?.graceActive);
  const graceSecondsLeft = status?.graceSecondsLeft || 0;
  const isBridgeActive = isSquadraConnected && isOperatoreConnected;

  const copyConfig = () => {
    const text = `Host: ${host}\nPorta: ${squadraPort}\nModalità: TCP Client`;
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const formatBytes = (bytes: number) => {
    if (!bytes) return '0 B';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  };

  return (
    <div className="space-y-3.5 pt-2">
      {/* Banner Stato Generale del Ponte */}
      {isBridgeActive ? (
        <div className="rounded-2xl border border-emerald-300 bg-linear-to-r from-emerald-500/15 via-emerald-500/5 to-transparent p-4 flex items-center justify-between gap-3 shadow-xs animate-fade-in">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-emerald-600 text-white flex items-center justify-center shrink-0 shadow-xs">
              <ArrowDownUp className="w-5 h-5 animate-pulse" />
            </div>
            <div>
              <div className="font-extrabold text-sm text-emerald-950 flex items-center gap-1.5">
                <span className="w-2.5 h-2.5 rounded-full bg-emerald-500 animate-ping" />
                Ponte Seriale COM Attivo
              </div>
              <div className="text-xs text-emerald-800/80 mt-0.5">
                Comunicazione bidirezionale in tempo reale tra TCPUART e Operatore
              </div>
            </div>
          </div>
          <button
            type="button"
            onClick={refresh}
            className="btn-ghost text-emerald-800 hover:bg-emerald-100 p-2 rounded-xl text-xs"
            title="Aggiorna stato"
          >
            <RotateCw className="w-4 h-4" />
          </button>
        </div>
      ) : isGraceActive ? (
        <div className="rounded-2xl border border-amber-300 bg-amber-50/90 p-3.5 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2.5">
            <AlertCircle className="w-5 h-5 text-amber-600 shrink-0 animate-pulse" />
            <div className="text-xs text-amber-900">
              <strong>Riconnessione operatore in attesa ({graceSecondsLeft}s):</strong> la sessione TCPUART viene mantenuta aperta.
            </div>
          </div>
          <button
            type="button"
            onClick={refresh}
            disabled={loading}
            className="btn-ghost text-amber-800 hover:bg-amber-100 p-2 rounded-xl text-xs"
            title="Aggiorna stato"
          >
            <RotateCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      ) : isOperatoreConnected ? (
        <div className="rounded-2xl border border-sky-300 bg-sky-50/90 p-3.5 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2.5">
            <CheckCircle2 className="w-5 h-5 text-sky-600 shrink-0" />
            <div className="text-xs text-sky-950">
              <strong>Operatore pronto in centrale:</strong> la porta TCP <strong>{squadraPort}</strong> è aperta, premi <strong>Connect</strong> su TCPUART.
            </div>
          </div>
          <button
            type="button"
            onClick={refresh}
            disabled={loading}
            className="btn-ghost text-sky-800 hover:bg-sky-100 p-2 rounded-xl text-xs"
            title="Aggiorna stato"
          >
            <RotateCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      ) : (
        <div className="rounded-2xl border border-slate-200 bg-slate-50/70 p-3.5 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2.5">
            <Radio className="w-5 h-5 text-slate-400 shrink-0" />
            <div className="text-xs text-slate-600">
              <strong>In attesa dell&apos;operatore in centrale (SSH porta {operatorePort}):</strong> la porta {squadraPort} per TCPUART si aprirà automaticamente appena l&apos;operatore si collega.
            </div>
          </div>
          <button
            type="button"
            onClick={refresh}
            disabled={loading}
            className="btn-ghost text-slate-600 hover:bg-slate-200/70 p-2 rounded-xl text-xs"
            title="Aggiorna stato"
          >
            <RotateCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      )}

      {/* Schede dei due lati del ponte: Regolatore (Squadra) e Centrale (Operatore) */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {/* Lato Squadra / Regolatore */}
        <div className={`rounded-2xl border p-4 transition ${
          isSquadraConnected
            ? 'border-emerald-300 bg-emerald-50/50 shadow-xs'
            : 'border-slate-200 bg-white'
        }`}>
          <div className="flex items-center justify-between mb-2.5">
            <div className="flex items-center gap-2">
              <div className={`w-8 h-8 rounded-xl flex items-center justify-center ${
                isSquadraConnected ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-500'
              }`}>
                <Smartphone className="w-4 h-4" />
              </div>
              <div>
                <div className="text-xs font-bold text-slate-900">Regolatore (Squadra)</div>
                <div className="text-[10px] text-slate-500">Porta TCP {squadraPort}</div>
              </div>
            </div>
            <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-extrabold ${
              isSquadraConnected
                ? 'bg-emerald-600 text-white shadow-2xs'
                : 'bg-slate-100 text-slate-600'
            }`}>
              {isSquadraConnected ? (
                <>
                  <CheckCircle2 className="w-3 h-3" /> COLLEGATO
                </>
              ) : (
                'IN ATTESA'
              )}
            </span>
          </div>

          <div className="text-xs text-slate-600 space-y-1 pt-1 border-t border-slate-100">
            <div className="flex justify-between">
              <span className="text-slate-400">Byte dal regolatore (RX):</span>
              <span className="font-mono font-bold text-slate-800">{formatBytes(status?.bytesRx || 0)}</span>
            </div>
            {status?.squadraRemoteAddress && (
              <div className="flex justify-between text-[11px]">
                <span className="text-slate-400">IP Smartphone:</span>
                <span className="font-mono text-slate-600 truncate max-w-[140px]">{status.squadraRemoteAddress}</span>
              </div>
            )}
          </div>
        </div>

        {/* Lato Operatore / Centrale */}
        <div className={`rounded-2xl border p-4 transition ${
          isOperatoreConnected
            ? 'border-emerald-300 bg-emerald-50/50 shadow-xs'
            : 'border-slate-200 bg-white'
        }`}>
          <div className="flex items-center justify-between mb-2.5">
            <div className="flex items-center gap-2">
              <div className={`w-8 h-8 rounded-xl flex items-center justify-center ${
                isOperatoreConnected ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-500'
              }`}>
                <Monitor className="w-4 h-4" />
              </div>
              <div>
                <div className="text-xs font-bold text-slate-900">Centrale (Operatore)</div>
                <div className="text-[10px] text-slate-500">Porta TCP {operatorePort}</div>
              </div>
            </div>
            <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-extrabold ${
              isOperatoreConnected
                ? 'bg-emerald-600 text-white shadow-2xs'
                : 'bg-slate-100 text-slate-600'
            }`}>
              {isOperatoreConnected ? (
                <>
                  <CheckCircle2 className="w-3 h-3" /> COLLEGATO
                </>
              ) : (
                'IN ATTESA'
              )}
            </span>
          </div>

          <div className="text-xs text-slate-600 space-y-1 pt-1 border-t border-slate-100">
            <div className="flex justify-between">
              <span className="text-slate-400">Byte dall'operatore (TX):</span>
              <span className="font-mono font-bold text-slate-800">{formatBytes(status?.bytesTx || 0)}</span>
            </div>
            {status?.operatoreRemoteAddress && (
              <div className="flex justify-between text-[11px]">
                <span className="text-slate-400">IP Operatore:</span>
                <span className="font-mono text-slate-600 truncate max-w-[140px]">{status.operatoreRemoteAddress}</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Istruzioni operative rapide per la squadra */}
      <div className="rounded-2xl border border-indigo-200 bg-indigo-50/40 p-4 space-y-3">
        <div className="flex items-start justify-between gap-2">
          <div className="flex items-center gap-2 text-indigo-950 font-bold text-xs">
            <HelpCircle className="w-4 h-4 text-indigo-600" />
            <span>Come collegarsi con l'adattatore seriale (PL2303 / FTDI / CP2102)</span>
          </div>
          <button
            type="button"
            onClick={copyConfig}
            className="inline-flex items-center gap-1 bg-white hover:bg-indigo-50 text-indigo-700 px-2.5 py-1 rounded-lg border border-indigo-200 text-[11px] font-semibold transition shrink-0 shadow-2xs"
          >
            {copied ? <Check className="w-3 h-3 text-emerald-600" /> : <Copy className="w-3 h-3" />}
            {copied ? 'Copiato!' : 'Copia Parametri'}
          </button>
        </div>

        <ol className="text-xs text-slate-700 space-y-2 list-decimal list-inside leading-relaxed">
          <li>
            Collega il cavo <strong>OTG Type-C</strong> al telefono e l'adattatore seriale (es. <strong>PL2303</strong>) alla porta diagnostica del regolatore.
          </li>
          <li>
            Apri l'app Android <strong>TCPUART transparent Bridge</strong> (o qualsiasi app di bridge Seriale-TCP come <em>TCP Serial Bridge</em>).
          </li>
          <li>
            Nell'app imposta la velocità seriale (<strong>9600</strong> per Swarco SPOT o <strong>19200</strong> per SCAE, 8N1).
          </li>
          <li>
            Seleziona la modalità <strong>TCP Client</strong> e inserisci:
            <div className="mt-1.5 p-2 rounded-xl bg-white border border-indigo-100 font-mono text-[11px] text-slate-800 space-y-0.5">
              <div>Host: <strong className="text-indigo-600">{host}</strong></div>
              <div>Porta: <strong className="text-indigo-600">{squadraPort}</strong></div>
            </div>
          </li>
          <li>
            Tocca <strong>Connect</strong>: lo stato sopra diventerà immediatamente verde <span className="font-bold text-emerald-700">COLLEGATO</span> e l'operatore potrà comunicare con il regolatore da remoto.
          </li>
        </ol>
      </div>

      {/* Pulsanti Azioni */}
      <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-2.5 pt-1">
        <button
          type="button"
          onClick={resetBridge}
          disabled={resetting}
          className="btn-secondary text-xs text-rose-700 hover:bg-rose-50 border-rose-200 px-3 py-2 rounded-xl font-medium transition justify-center"
          title="Chiude forzatamente i socket attivi e azzera i contatori"
        >
          {resetting ? 'Reset in corso…' : 'Resetta Connessioni Ponte'}
        </button>

        <button
          type="button"
          onClick={refresh}
          disabled={loading}
          className="btn-primary text-xs px-3.5 py-2 rounded-xl font-bold transition flex items-center justify-center gap-1.5"
        >
          <RotateCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
          Aggiorna Stato
        </button>
      </div>

      {error && (
        <div className="text-xs text-rose-600 flex items-center gap-1.5 bg-rose-50 p-2.5 rounded-xl border border-rose-200">
          <AlertCircle className="w-4 h-4 shrink-0" />
          <span>{error}</span>
        </div>
      )}
    </div>
  );
}
